import { useState } from "react";
import { apiUrls } from "../../actprep-ui/utils/urls";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import ButtonGroup from "@mui/material/ButtonGroup";
import { DatePicker } from "@mui/x-date-pickers-pro";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import dayjs from "dayjs";
import { standardDateFormat } from "../../actprep-ui/utils/calendar";
import api from "../../actprep-ui/utils/api";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { useConfirm } from "material-ui-confirm";

const findCommonProgram = (students) => {
  const programAppearanceMap = new Map();
  students.forEach((student) => {
    student.ordered_programs.forEach((orderedProgram) => {
      const key = `${orderedProgram.program.uuid}__${orderedProgram.start_date}`;
      const currentInfo = programAppearanceMap.get(key) || {
        orderedProgram: orderedProgram,
        count: 0,
      };
      const newInfo = {
        ...currentInfo,
        count: currentInfo.count + 1,
      };
      programAppearanceMap.set(key, newInfo);
    });
  });
  for (let appearanceInfo of programAppearanceMap.values()) {
    if (appearanceInfo.count === students.length) {
      return appearanceInfo.orderedProgram;
    }
  }
  return null;
};
export default function StudentsToProgramForm({
  students,
  onActionComplete,
  action = "add",
}) {
  if (!["add", "remove"].includes(action)) {
    throw Error(`Invalid action value: ${action}`);
  }
  const commonProgram =
    action === "remove" ? findCommonProgram(students) : null;

  const [formData, setFormData] = useState(
    commonProgram
      ? {
          program: {
            uuid: commonProgram.program.uuid,
            label: commonProgram.program.title,
          },
          start_date: dayjs(commonProgram.start_date),
        }
      : {
          program: null,
          start_date: null,
        }
  );

  const [error, setError] = useState(null);
  const confirm = useConfirm();

  const formatDate = (date) => {
    const dateObj = dayjs(date);
    return dateObj.toDate().toLocaleDateString();
  };
  const updateFormData = (key) => (val) => {
    setFormData((formData) => ({
      ...formData,
      [key]: val,
    }));
  };

  const handleAddStudentsToProgram = async () => {
    const response = await api.post(apiUrls.admin.studentToProgram(), {
      student_uuids: students.map((student) => student.uuid),
      program_uuid: formData.program.uuid,
      start_date: standardDateFormat(formData.start_date.toDate()),
    });
    if (response.data?.success) {
      showSuccessSnackbar(
        `${students.length} students added to ${formData.program.label}!`
      );
      if (onActionComplete) {
        onActionComplete();
      }
      return;
    }
    setError(response.data?.reason);
  };

  const handleRemoveStudentsFromProgram = async () => {
    const response = await api.delete(apiUrls.admin.studentToProgram(), {
      data: {
        student_uuids: students.map((student) => student.uuid),
        program_uuid: formData.program.uuid,
        start_date: standardDateFormat(formData.start_date.toDate()),
      },
    });

    if (response.data?.success) {
      showSuccessSnackbar(
        `${students.length} students removed from ${formData.program.label}!`
      );
      if (onActionComplete) {
        onActionComplete();
      }
      return;
    }
    setError(response.data?.reason);
  };

  const actionFunction =
    action === "add"
      ? handleAddStudentsToProgram
      : handleRemoveStudentsFromProgram;

  const handleFormSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const text =
      action === "add"
        ? `Are you sure you want to add ${students.length} student(s) to ${
            formData.program?.label
          }, starting on ${formatDate(formData.start_date)}?`
        : `Are you sure you want to remove ${students.length} student(s) from ${
            formData.program?.label
          }, starting on ${formatDate(formData.start_date)}?`;
    confirm({
      description: text,
    }).then(() => {
      actionFunction();
    });
  };

  return (
    <>
      <Box component={"form"} onSubmit={handleFormSubmit}>
        <Grid
          container
          spacing={1}
          sx={{
            width: "30rem",
            maxWidth: "100%",
          }}
        >
          <Grid size={12}>
            <ModelAutoComplete
              required
              modelName={"Program"}
              label={"Program"}
              value={formData.program}
              setValue={updateFormData("program")}
            />
          </Grid>
          <Grid size={12}>
            <DatePicker
              value={formData.start_date}
              onChange={updateFormData("start_date")}
              slotProps={{ textField: { variant: "standard", required: true } }}
              sx={{
                width: "100%",
              }}
              label="Start Date"
              orientation="landscape"
            />
          </Grid>
          <Grid size={12}>
            {error && <Alert severity="error">{error}</Alert>}
          </Grid>
        </Grid>
        <ButtonGroup
          fullWidth
          size="small"
          variant="standard"
          sx={{
            margin: "0 auto",
          }}
        >
          <Button type="submit">
            {action === "add" ? "Add to Program" : "Remove from Program"}
          </Button>
        </ButtonGroup>
      </Box>
    </>
  );
}
